import { createStore as reduxCreateStore, applyMiddleware, compose, combineReducers } from 'redux'
import createSagaMiddleware from 'redux-saga'
import createDebounce from 'redux-debounced'
import { staticReducers, staticSagas } from 'client/store'
import loginModal from 'client/store/login-modal'
import createQueryStringMiddleware from 'client/middlewares/query-string'

// Slices that must exist from the first dispatch, even though the component that
// owns them is code-split.
//
// The login modal is opened from anywhere (navbar, paywalls, "Start Free Trial"
// CTAs) but only mounts — and only injects its slice — once `shownModals`
// includes it. Anything a CTA dispatches on the way there (`setMode('register')`,
// `setCheckoutIntent(...)`) landed in a store with no `loginModal` reducer and was
// dropped, so the modal opened in *login* mode with no intent to resume: signups
// from pages that don't list the slice themselves (/search, /features, …) created
// an account but never started the Basic trial — see SCI-3869.
//
// Injecting here rather than in `staticReducers` keeps `injectSaga`'s per-key
// dedupe in play, so the page-level `injectAsyncStore({ loginModal })` calls that
// already exist stay no-ops instead of starting a second copy of the saga.
const ALWAYS_INJECTED_REDUCERS = { loginModal }

function createReducer(asyncReducers) {
  return combineReducers({
    ...staticReducers,
    ...asyncReducers
  })
}

function createSagaInjector(runSaga) {
  const injectedSagas = {}

  const injectSaga = (key, saga) => {
    if (injectedSagas[key]) return injectedSagas[key]

    const task = runSaga(saga)
    injectedSagas[key] = task
    return task
  }

  return injectSaga
}

export const createStore = (initialState = {}, history, injectedReducers = {}) => {
  const extraReducers = { ...ALWAYS_INJECTED_REDUCERS, ...injectedReducers }
  const sagaMiddleware = createSagaMiddleware()
  const debounceMiddleware = createDebounce()

  const middleware = [sagaMiddleware, debounceMiddleware]

  if (typeof window !== 'undefined') {
    middleware.push(createQueryStringMiddleware({ history }))
  }

  const devToolsEnhancer =
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true })
  const composeEnhancers = devToolsEnhancer || compose
  const store = reduxCreateStore(
    createReducer(extraReducers),
    initialState,
    composeEnhancers(applyMiddleware(...middleware))
  )

  //
  // Add a dictionary to keep track of the registered async reducers
  //
  store.asyncReducers = extraReducers

  //
  // Create an inject reducer function
  // This function adds the async reducer, and creates a new combined reducer
  //
  store.injectReducers = asyncReducers => {
    Object.keys(asyncReducers).forEach(key => {
      if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducers[key]
      }
    })
    store.replaceReducer(createReducer(store.asyncReducers))
  }

  store.injectSaga = createSagaInjector(sagaMiddleware.run)

  const rootTask = store.injectSaga('root', staticSagas)
  const asyncTasks = Object.entries(extraReducers)
    .filter(([_, storeSlice]) => storeSlice.saga)
    .map(([key, storeSlice]) => store.injectSaga(key, storeSlice.saga))

  return { store, sagaTasks: [rootTask, ...asyncTasks] }
}

export default createStore
